import { useSyncExternalStore } from "react";

/** A phone held upright: tabs along the bottom, one bar at the top and sheets from below. */
export const PHONE_QUERY = "(max-width: 700px)";

/**
 * A touch screen up to iPad size, or a narrow window with a mouse: a rail down the left with a name under
 * each icon. Kept in step with the same media query in phone.css.
 */
export const RAIL_QUERY =
  "(min-width: 701px) and (max-width: 1180px) and (hover: none), (min-width: 701px) and (max-width: 860px)";

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (changed) => {
      const m = window.matchMedia(query);
      m.addEventListener("change", changed);
      return () => m.removeEventListener("change", changed);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** A phone: the tabs along the bottom. */
export const usePhone = (): boolean => useMediaQuery(PHONE_QUERY);
/** An iPad (or a narrow window): the rail down the left. */
export const useRail = (): boolean => useMediaQuery(RAIL_QUERY);
/**
 * Screens drawn the native way: a phone, and an iPad, which draws the same screens with more room and puts
 * a list beside its detail. A computer keeps its own pages.
 */
export function useNative(): boolean {
  const phone = usePhone();
  const rail = useRail();
  return phone || rail;
}

/** Opened from its own icon on the home screen, with no browser around it. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || !!window.matchMedia?.("(display-mode: standalone)")?.matches;
}

/** To the top of the screen. Why: on a phone or an iPad the app scrolls inside `.pn-main`, not the window. */
export function scrollToTop(): void {
  document.querySelector<HTMLElement>(".pn-app > .pn-main")?.scrollTo({ top: 0 });
  window.scrollTo(0, 0);
}
